import { Button, PrimaryButton, FooterButton } from '../button';
import { FormControl, Icon, Input, Stack } from '../../globals';
import { MOBILE_BREAKPOINT_PX } from '../../../shared/helpers';
import React, {
	FunctionComponent,
	Suspense,
	createContext,
	createRef,
	useContext,
	useEffect,
	useState,
	ReactElement,
	isValidElement,
} from 'react';

import { ReactComponent as CloseIcon } from '../../../shared/icons/times-solid.svg';
import { ReactComponent as InfoIcon } from '../../../shared/icons/modal/info.svg';
import { ReactComponent as SuccessIcon } from '../../../shared/icons/modal/success.svg';
import { ReactComponent as WarningIcon } from '../../../shared/icons/modal/warning.svg';
import { ReactComponent as ErrorIcon } from '../../../shared/icons/modal/error.svg';
import { ReactComponent as ErrorMandatoryIcon } from '../../../shared/icons/modal/mandatory.svg';
import { ReactComponent as ErrorProfanityIcon } from '../../../shared/icons/modal/profanity.svg';
import { createPortal } from 'react-dom';
import { dialogsPortal } from '../../../shared/portals';
import { omit } from 'lodash';
import styled, { css } from 'styled-components';
import { useAppStore } from '../../../state/store';
import { useZakekeTranslations } from '@zakeke/zakeke-customizer-react';
import Tooltip from '../tooltip';

export const dialogContext = createContext({ dialogId: '' });

export function useDialogManager() {
	const [addDialog, removeDialog] = useAppStore((x) => [x.addDialog, x.removeDialog]);
	const { dialogId } = useContext(dialogContext);

	return {
		currentDialogId: dialogId,
		showDialog: addDialog,
		closeDialog: removeDialog,
	};
}

// #region Styled comp

const DialogOverlay = styled.div`
	position: fixed;
	background-color: ${(props) => props.theme.dialogs.backdrop};
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: flex-start;
	padding: 40px;
	color: ${(props) => props.theme.colors.textMain};
	overflow: auto;
	z-index: 51;

	@media screen and (max-width: ${MOBILE_BREAKPOINT_PX}) {
		padding: 20px;
		background-color: rgba(0, 0, 0, 0.5);
	}
`;

const DialogWindowContainer = styled.div<{ $hasTitle?: boolean }>`
	margin-block: auto;
	background-color: transparent;
	flex-basis: 800px;
	display: flex;
	flex-direction: column;
	position: relative;
	max-width: 100%;
`;

const DialogWindowClose = styled(Icon)`
	cursor: pointer;
	font-size: 20px;
	color: ${(props) => props.theme.colors.iconMain};

	svg {
		width: 20px;
		height: 20px;
	}
	@media (hover) {
		&:hover {
			color: ${(props) => props.theme.colors.primary};
		}
	}
	@media screen and (max-width: ${MOBILE_BREAKPOINT_PX}) {
		right: 25px;
		top: 25px;
	}
`;

// Side padding is for select outlines that must not be cropped
const DialogContent = styled.div<{ $hasFooter?: boolean; $flex?: boolean }>`
	color: ${(props) => props.theme.colors.textMain};
	background-color: ${(props) => props.theme.colors.backgroundMain};
	max-height: calc(100vh - 270px);
	padding: 0 24px;
	overflow-y: auto;
	flex: 1;
	min-height: 0;
	position: relative;
	& * {
		max-width: 100%;
	}

	${(props) =>
		props.$flex &&
		css`
			display: flex;
		`};

	@media screen and (max-width: ${MOBILE_BREAKPOINT_PX}) {
		max-height: calc(100vh - 250px);
	}
`;

const dialogFooterButtonCss = css`
	min-width: 100px;

	@media screen and (max-width: 768px) {
		font-size: 12px;
		min-width: 50px;
		padding: 10px;
	}
`;

const DialogFooterIconButton = styled(FooterButton)`
	${dialogFooterButtonCss}
`;

const DialogFooterPrimaryButton = styled(PrimaryButton)`
	${dialogFooterButtonCss}
`;

const DialogFooterButton = styled(Button)<{ outline?: boolean }>`
	${dialogFooterButtonCss};
	color: ${(props) => (props.outline ? props.theme.colors.textMain : props.theme.colors.primary)};
	font-weight: bold;

	&:hover {
		opacity: 0.7;
	}
`;

export const DialogFooter = styled.div<{ $justifyContent?: string }>`
	display: flex;
	position: relative;
	align-items: center;
	flex-wrap: wrap;
	gap: 20px;
	padding: 22px 24px 0 24px;
	color: ${(props) => props.theme.colors.textMain};
	justify-content: ${(props) => props.$justifyContent || 'flex-end'};

	@media screen and (max-width: 768px) {
		gap: 10px;
	}
`;

const DialogLeftFooterCustomComponentContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	flex: 1;
`;

const DialogTitleContainer = styled(Stack)`
	align-items: center;
	padding-bottom: 12px;
	border-bottom: 1px solid ${(props) => props.theme.colors.borders};
`;

const DialogTitle = styled.h1`
	font-size: 20px;
	line-height: 28px;
	text-align: start;
	margin: 0;
	padding: 0;
	width: 100%;
`;

const DialogTitlePlaceholder = styled.div`
	min-height: 34px;
	width: 100%;
`;

export const DialogContainer = styled.div<{ $hasTitle?: boolean; $variant?: 'window' | 'panel'; $opened?: boolean }>`
	padding-top: ${'12px' /*(props) => (props.$hasTitle ? '24px' : '0')*/};
	padding-bottom: 24px;
	margin-block: auto;
	color: ${(props) => props.theme.colors.textMain};
	background-color: ${(props) => props.theme.colors.backgroundMain};
	border-radius: calc(${(props) => props.theme.measures.borderRadius} * 6);
	box-shadow: ${(props) => props.theme.dialogs.shadows};
	overflow: hidden;

	${(props) =>
		props.$variant === 'panel' &&
		css`
			display: flex;
			flex-direction: column;
			justify-content: start;
			position: fixed;
			left: 15px;
			right: 15px;
			bottom: 0;
			min-height: 75vh;
			max-height: 75vh;
			padding-top: 12px;
			border-radius: calc(${(props) => props.theme.measures.borderRadius} * 6)
				calc(${(props) => props.theme.measures.borderRadius} * 6) 0 0;
			border: 1px solid ${(props) => props.theme.colors.borders};
			border-bottom-width: 0;
			transition: all ease 0.3s;
			z-index: 901;
			transform: translateY(100%);

			${() =>
				props.$opened &&
				css`
					&:not(:empty) {
						transform: translateY(0);
						transition-duration: 0.25s;
						transition-delay: 0.05s;
						box-shadow: ${props.theme.panels.shadows};
					}
				`}
		`}
`;

// #region Dialog window

interface DialogWindowProps {
	className?: string;
	showCloseButton?: boolean;
	onClose: () => void;
	children?: React.ReactNode;
	align?: 'center' | 'left' | 'right';
	$hasTitle?: boolean;
}

export const DialogWindow: FunctionComponent<DialogWindowProps> = ({
	children,
	className,
	showCloseButton = true,
	onClose,
	$hasTitle,
}) => {
	return (
		<DialogWindowContainer className={className} $hasTitle={$hasTitle}>
			{children}
		</DialogWindowContainer>
	);
};

export interface DialogButton {
	label?: string;
	outline?: boolean;
	primary?: boolean;
	disabled?: boolean;
	small?: boolean;
	dataTrack?: string;
	tooltip?: string;
	onClick?: () => void;
	icon?: ReactElement;
}

interface DialogProps {
	showCloseButton?: boolean;
	children?: React.ReactNode;
	onClose?: () => void;
	title?: string;
	windowDecorator?: React.FunctionComponent<any>;
	buttons?: (DialogButton | ReactElement)[];
	leftFooterCustomComponent?: React.ReactNode;
	alignTop?: boolean;
	align?: 'center' | 'left' | 'right';
	justifyFooter?: 'center' | 'flex-start' | 'flex-end' | 'space-between';
	variant?: 'window' | 'panel';
	flexContent?: boolean;
}

// const DivAlignRight = styled.div`
// 	justify-content: flex-end;
// 	display: flex;
// `;

interface MessageDialogProps {
	showButtons?: boolean;
	message: React.ReactNode;
}

// #region Dialog Comp
export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>((props, ref) => {
	const Window = props.windowDecorator || DialogWindow;
	const [removeDialog] = useAppStore((x) => [x.removeDialog]);
	const { dialogId } = useContext(dialogContext);
	const [internalOpened, setInternalOpened] = useState(false);

	useEffect(() => {
		const timeout = props.variant === 'panel' ? 150 : 0;
		setTimeout(() => {
			setInternalOpened(true);
		}, timeout);
	}, [props.variant]);

	const close = () => {
		setInternalOpened(false);
		const timeout = props.variant === 'panel' ? 150 : 0;
		setTimeout(() => {
			if (props.onClose) props.onClose();
			removeDialog(dialogId);
		}, timeout);
	};
	return (
		<DialogOverlay
		// Scommentare se richiesta la chiusura del dialog cliccando sul backdrop
		// onClick={(e) => {
		// 	e.preventDefault();
		// 	if (e.currentTarget === e.target && props.showCloseButton !== false) onClose();
		// }}
		>
			{React.createElement(
				Window,
				{ onClose: close, showCloseButton: props.showCloseButton, $hasTitle: !!props.title, id: 'window' },
				<React.Fragment>
					<DialogContainer $hasTitle={!!props.title} $variant={props.variant} $opened={internalOpened}>
						{
							/*!!props.title && ( */
							<DialogTitleContainer
								$horizontal={true}
								$marginLeft={24}
								$marginRight={24}
								$marginBottom={22}
								$gap={50}
							>
								{props.title ? <DialogTitle>{props.title}</DialogTitle> : <DialogTitlePlaceholder />}
								{props.showCloseButton && (
									<DialogWindowClose onClick={close}>
										<CloseIcon />
									</DialogWindowClose>
								)}
							</DialogTitleContainer>
							/* )} */
						}
						<DialogContent
							ref={ref}
							$hasFooter={props.buttons && props.buttons?.length > 0}
							$flex={props.flexContent}
						>
							{/*props.showCloseButton && !props.title?.length && (
								<DivAlignRight>
									<DialogWindowClose onClick={onClose}>
										<CloseIcon />
									</DialogWindowClose>
								</DivAlignRight>
							)*/}
							<Suspense fallback={'Loading...'}>{props.children}</Suspense>
						</DialogContent>
						{props.buttons && props.buttons.length > 0 && (
							<>
								<DialogFooter $justifyContent={props.justifyFooter}>
									{props.leftFooterCustomComponent && (
										<DialogLeftFooterCustomComponentContainer>
											{props.leftFooterCustomComponent}
										</DialogLeftFooterCustomComponentContainer>
									)}

									{props.buttons.map((button) => {
										if (isValidElement(button)) return button;
										return printModalFooterButtons(button as DialogButton); // force cast on button, since vscode doesn't recognize the prevous line type guard fn
									})}
								</DialogFooter>
							</>
						)}
					</DialogContainer>
				</React.Fragment>,
			)}
		</DialogOverlay>
	);
});

export const printModalFooterButtons = (button: DialogButton) => {
	const ButtonComponent = button.primary
		? DialogFooterPrimaryButton
		: button.icon
		? DialogFooterIconButton
		: DialogFooterButton;

	return (
		<Tooltip key={`${button.label}-tip`} addContainer html={button.tooltip} interactive={!!button.tooltip}>
			<ButtonComponent
				key={`${button.label}-btn`}
				outline={button.outline}
				disabled={button.disabled}
				data-track={button.dataTrack}
				onClick={() => button.onClick?.()}
				small={button.small}
				icon={button.icon}
			>
				{button.label}
			</ButtonComponent>
		</Tooltip>
	);
};

export const DialogsRenderer: FunctionComponent<{}> = (props) => {
	const [dialogs] = useAppStore((x) => [x.dialogs]);
	return (
		<>
			{createPortal(
				dialogs.map(([id, dialog]) => (
					<dialogContext.Provider key={id} value={{ dialogId: id }}>
						{dialog}
					</dialogContext.Provider>
				)),
				dialogsPortal,
			)}
		</>
	);
};

// #region Basic dialogs

export const BasicDialogWindow = styled(DialogWindow)`
	max-width: 600px;
	max-height: calc(100vh - 80px);
`;

const MessageContent = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	grid-area: message-content;
	margin: 2px 0;
`;

const IconMessageContainer = styled.div`
	display: grid;
	gap: 16px;
	margin: 2px 0;
	grid-template-areas: 'icon message-content';
	grid-template-columns: 48px 1fr;
	& ${MessageContent} {
		align-items: flex-start;
		margin: 0;
	}
	@media (max-width: ${MOBILE_BREAKPOINT_PX}) {
		grid-template-columns: 36px 1fr;
	}
`;

const IconContent = styled.div<{ $type: 'info' | 'success' | 'warning' | 'error' | 'mandatory' | 'profanity' }>`
	grid-area: icon;
	& svg {
		fill: currentColor;
	}
	${(props) => css`
		color: ${props.$type === 'info'
			? '#007FFF'
			: props.$type === 'success'
			? '#008556'
			: props.$type === 'warning'
			? '#E86825'
			: '#DE1C22'};
	`}
`;

// usata solo qui dentro per generare le altre message dialog
const GenericMessageDialog: FunctionComponent<DialogProps & { showButtons?: boolean }> = ({
	children,
	showButtons,
	...props
}) => {
	const { closeDialog } = useDialogManager();
	const { dialogId } = useContext(dialogContext);

	return (
		<Dialog
			showCloseButton={true}
			windowDecorator={BasicDialogWindow}
			buttons={
				showButtons !== false
					? [
							{
								label: 'OK',
								primary: true,
								small: true,
								onClick: () => {
									closeDialog(dialogId);
									props.onClose?.();
								},
							},
					  ]
					: []
			}
			{...props}
		>
			{children}
		</Dialog>
	);
};

const GenericIconDialog: FunctionComponent<
	{ type: 'info' | 'success' | 'warning' | 'error' | 'profanity' | 'mandatory' } & DialogProps & MessageDialogProps
> = ({ message, type = 'info', ...props }) => {
	const icon = (() => {
		switch (type) {
			case 'info':
				return <InfoIcon />;
			case 'success':
				return <SuccessIcon />;
			case 'warning':
				return <WarningIcon />;
			case 'error':
				return <ErrorIcon />;
			case 'profanity':
				return <ErrorProfanityIcon />;
			case 'mandatory':
				return <ErrorMandatoryIcon />;
			default:
				return <InfoIcon />;
		}
	})();
	return (
		<GenericMessageDialog {...props}>
			<IconMessageContainer>
				<IconContent $type={type}>{icon}</IconContent>
				<MessageContent>{message}</MessageContent>
			</IconMessageContainer>
		</GenericMessageDialog>
	);
};

export const MessageDialog: FunctionComponent<DialogProps & MessageDialogProps> = ({ message, ...props }) => {
	return (
		<GenericMessageDialog {...props}>
			<MessageContent>{message}</MessageContent>
		</GenericMessageDialog>
	);
};

export const InfoMessageDialog: FunctionComponent<DialogProps & MessageDialogProps> = ({ ...props }) => (
	<GenericIconDialog type='info' {...props} />
);
export const SuccessMessageDialog: FunctionComponent<DialogProps & MessageDialogProps> = ({ ...props }) => (
	<GenericIconDialog type='success' {...props} />
);
export const WarningMessageDialog: FunctionComponent<DialogProps & MessageDialogProps> = ({ ...props }) => (
	<GenericIconDialog type='warning' {...props} />
);
export const ErrorMessageDialog: FunctionComponent<DialogProps & MessageDialogProps> = ({ ...props }) => (
	<GenericIconDialog type='error' {...props} />
);
export const ProfanityMessageDialog: FunctionComponent<DialogProps & MessageDialogProps> = ({ ...props }) => (
	<GenericIconDialog type='profanity' {...props} />
);
export const MandatoryMessageDialog: FunctionComponent<DialogProps & MessageDialogProps> = ({ ...props }) => (
	<GenericIconDialog type='mandatory' {...props} />
);

export const QuestionDialog: FunctionComponent<
	{
		message: React.ReactNode;
		buttonYesLabel?: string;
		buttonNoLabel?: string;
		onYesClick?: () => void;
		onNoClick?: () => void;
	} & DialogProps
> = ({ message, buttonYesLabel, buttonNoLabel, onYesClick, onNoClick, title, ...props }) => {
	const { closeDialog } = useDialogManager();
	const { dialogId } = useContext(dialogContext);
	const { T } = useZakekeTranslations();

	return (
		<WarningMessageDialog
			title={title}
			message={message}
			buttons={[
				{
					label: buttonNoLabel || T._('No'),
					outline: true,
					small: true,
					onClick: onNoClick || (() => closeDialog(dialogId)),
				},
				{
					label: buttonYesLabel || T._('Yes'),
					primary: true,
					small: true,
					onClick: onYesClick || (() => closeDialog(dialogId)),
				},
			]}
			{...props}
		/>
	);
};

type InputControlProps = React.HTMLProps<HTMLInputElement>;

type InputDialogProps = {
	label: string;
	onConfirm: (value: string) => void;
	buttonLabel?: string;
} & DialogProps &
	InputControlProps;

export const InputDialog: FunctionComponent<InputDialogProps> = ({
	label,
	defaultValue,
	buttonLabel,
	onConfirm,
	...props
}) => {
	const { T } = useZakekeTranslations();
	const [value, setValue] = useState((defaultValue as string) || '');
	const inputProps = props as Omit<InputControlProps, 'ref' | 'as'>;
	const inputRef = createRef<HTMLInputElement>();

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') onConfirm(value);
	};

	useEffect(() => {
		inputRef.current?.focus();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Dialog
			windowDecorator={BasicDialogWindow}
			buttons={[
				{
					label: buttonLabel || T._('Confirm'),
					primary: true,
					small: true,
					onClick: () => {
						onConfirm(value);
					},
				},
			]}
			{...omit(props, 'ref')}
		>
			<FormControl label={label}>
				<Input
					ref={inputRef}
					onKeyDown={handleKeyDown}
					value={value}
					onChange={(e) => setValue(e.currentTarget.value)}
					{...inputProps}
				/>
			</FormControl>
		</Dialog>
	);
};

// #endregion
